import { useCallback, useRef, useState } from "react";

import { getCaseDocuments } from "@/services/api/case-checking";
import { useTaskProgressDialogOptional } from "@/context/TaskProgressDialogContext";
import { useCaseCheckingRealtime } from "@/components/case-checking/useCaseCheckingRealtime";

import type { NewCaseFileUploadFlowKind, NewCaseUploadedFile } from "./newCaseFileUploadTypes";
import { runNewCaseFileUploadFlow } from "./runNewCaseFileUploadFlow";

type UseCaseNewCaseFileUploadOptions = {
  companyId?: string;
  flowKind: NewCaseFileUploadFlowKind;
  mockData?: boolean;
};

export function useCaseNewCaseFileUpload({
  companyId,
  flowKind,
  mockData = false,
}: UseCaseNewCaseFileUploadOptions) {
  const [files, setFiles] = useState<NewCaseUploadedFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [activeCaseId, setActiveCaseId] = useState<string | null>(null);
  const taskProgressDialog = useTaskProgressDialogOptional();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadedFileNamesRef = useRef<Set<string>>(new Set());
  const classificationDoneRef = useRef<(() => void) | null>(null);

  const syncUploadDocsFromApi = useCallback(async (caseId: string) => {
    const uploadedNames = uploadedFileNamesRef.current;
    if (uploadedNames.size === 0) return false;

    const docs = await getCaseDocuments(caseId);
    setFiles((prev) =>
      prev.map((sf) => {
        const apiDoc = docs.find((d) => d.filename === sf.name);
        if (apiDoc?.classification) {
          return {
            ...sf,
            classification: apiDoc.classification,
            status: "classified" as const,
          };
        }
        return sf;
      }),
    );

    const classifiedCount = docs.filter(
      (d) => d.classification && uploadedNames.has(d.filename),
    ).length;
    const allClassified = classifiedCount >= uploadedNames.size;
    if (allClassified) {
      classificationDoneRef.current?.();
      classificationDoneRef.current = null;
    }
    return allClassified;
  }, []);

  useCaseCheckingRealtime({
    enabled: Boolean(activeCaseId) && !mockData,
    companyId,
    caseId: activeCaseId,
    onDocumentsChange: (caseId) => {
      void syncUploadDocsFromApi(caseId);
    },
  });

  const handleMockUpload = useCallback(() => {
    setFiles([
      { name: "Client_FactFind_2026.pdf", type: "fact-find", classification: "Fact Find", status: "classified" },
      { name: "FileNote_Meeting_Jan22.docx", type: "file-note", classification: "File Note", status: "classified" },
      { name: "SuitabilityReport_v3.pdf", type: "suitability", classification: "Suitability Report", status: "classified" },
    ]);
    setIsProcessing(true);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    const newFiles = Array.from(selected);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setFiles((prev) => [
      ...prev,
      ...newFiles.map((f) => ({
        name: f.name,
        type: "other" as const,
        classification: "",
        status: "pending" as const,
      })),
    ]);
  }, []);

  const updateFileRow = useCallback((index: number, patch: Partial<NewCaseUploadedFile>) => {
    setFiles((prev) => prev.map((f, idx) => (idx === index ? { ...f, ...patch } : f)));
  }, []);

  const submit = useCallback(async (): Promise<string | null> => {
    if (mockData) {
      handleMockUpload();
      return null;
    }

    if (selectedFiles.length === 0) {
      setError("Please select at least one file");
      return null;
    }

    setError(null);
    setIsProcessing(true);
    setProcessingStep("Creating case...");
    setFiles((prev) =>
      prev.map((f) => ({ ...f, classification: "Pending upload", status: "pending" as const })),
    );

    const uploadedFileNames = uploadedFileNamesRef.current;
    uploadedFileNames.clear();

    try {
      const { caseId, uploadedFileNames: uploadedNames } = await runNewCaseFileUploadFlow(selectedFiles, {
        kind: flowKind,
        onStep: setProcessingStep,
        onFileRow: updateFileRow,
        openTaskProgress: taskProgressDialog?.openTaskProgress,
      });

      setActiveCaseId(caseId);
      for (const name of uploadedNames) {
        uploadedFileNames.add(name);
      }

      setProcessingStep("");
      setIsProcessing(false);
      return caseId;
    } catch (err) {
      console.error("Failed to run new case file upload:", err);
      setError(
        err instanceof Error ? err.message : "Failed to create case. Please try again.",
      );
      setProcessingStep("");
      setIsProcessing(false);
      return null;
    }
  }, [
    flowKind,
    handleMockUpload,
    mockData,
    selectedFiles,
    taskProgressDialog,
    updateFileRow,
  ]);

  return {
    files,
    hasFiles: files.length > 0,
    isProcessing,
    processingStep,
    error,
    setError,
    fileInputRef,
    handleFileSelect,
    handleMockUpload,
    submit,
  };
}
